// §9.2 — the app manifest, served at `/.well-known/hangar-app.json` on the app's own origin.
//
// Two rules shape the schema and are the reason it is not just a type:
//
//  1. **`origin` must equal the URL the manifest was fetched from.** An app declares who it is,
//     and the only thing that can corroborate the claim is where it was served from. `parseManifest`
//     therefore takes the fetch URL and refuses to produce a manifest without one.
//  2. **Unknown top-level keys are preserved and ignored.** A manifest written for a later Hangar
//     must survive a round trip through this one, or the first field anybody adds silently deletes
//     itself from every cache (§9.6 caches the manifest locally).
import { z } from 'zod';
import type { MenuNode } from './api';

export const MANIFEST_SCHEMA_VERSION = 1 as const;

/** Where a manifest is always fetched from, relative to the app's origin. */
export const MANIFEST_WELL_KNOWN_PATH = '/.well-known/hangar-app.json';

/** §9.2 — `^[a-z0-9][a-z0-9-]{1,31}$`. */
export const APP_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,31}$/;

export const appIdSchema = z
  .string()
  .regex(APP_ID_PATTERN, 'app id must match ^[a-z0-9][a-z0-9-]{1,31}$');

/** §9.4 — the permission strings a manifest may request. */
export const PERMISSIONS = [
  'notifications',
  'clipboard.write',
  'share.send',
  'exec.provide',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** §9.4 — granted without a prompt for a catalog app. */
export const DEFAULT_GRANTED_PERMISSIONS: readonly Permission[] = [
  'notifications',
  'clipboard.write',
  'share.send',
];

// ── The pieces ─────────────────────────────────────────────────────────────────────────────

/**
 * An absolute `http(s)` URL. `z.url()` would accept `javascript:` and `data:`, either of which in
 * `entry` is a script running with the shell's own privileges.
 */
const httpUrlSchema = z.string().refine((value) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}, 'must be an absolute http(s) URL');

/** An origin, in the form `new URL(x).origin` produces: scheme + host + non-default port. */
export const originSchema = httpUrlSchema.refine(
  (value) => value === originOf(value),
  'must be a bare origin — no path, query or fragment',
);

/** The origin of a URL, or `null` when it is not one. */
export function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export const manifestIconSchema = z.object({
  src: z.string().min(1),
  size: z.number().int().positive(),
});

export const manifestWindowSchema = z.object({
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  minWidth: z.number().int().positive().optional(),
  minHeight: z.number().int().positive().optional(),
  resizable: z.boolean().optional(),
  multiWindow: z.boolean().optional(),
  /** §9.6 — may the shell unload this app's frame after 15 minutes minimized? */
  resumable: z.boolean().optional(),
});

export const manifestFileTypeSchema = z.object({
  ext: z.array(z.string().min(1)).min(1),
  mime: z.array(z.string().min(1)).optional(),
  role: z.enum(['editor', 'viewer']),
});

/** §10.3 — what this app will accept from a share or a drop. */
export const manifestAcceptSchema = z.object({
  mime: z.array(z.string().min(1)).min(1),
  action: z.enum(['open', 'import']),
});

export const manifestJumpListSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
});

/**
 * §9.3 `menu` — the same `MenuNode` §4.1 already declares, so a manifest's static default and a
 * runtime `publish` are the same tree and the skin renders one renderer's worth of it.
 *
 * Typed as `ZodType<unknown>` rather than `ZodType<MenuNode>`: `exactOptionalPropertyTypes` makes
 * zod's `.optional()` output (`T | undefined`) incompatible with an interface's `?:`, and pinning
 * the annotation would be a lie in the other direction. `menuTree` below is the checked crossing.
 */
export const menuNodeSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    id: z.string().min(1),
    label: z.string(),
    items: z.array(menuNodeSchema).optional(),
    accelerator: z.string().optional(),
    enabled: z.boolean().optional(),
    checked: z.boolean().optional(),
    separatorBefore: z.boolean().optional(),
  }),
);

/** A parsed menu tree as §4.1's type. Only ever called on a value `menuNodeSchema` accepted. */
export function menuTree(parsed: unknown): MenuNode[] {
  return (parsed ?? []) as MenuNode[];
}

/**
 * The known fields. **`.loose()`** rather than `.strict()`: §9.2 says unknown top-level keys are
 * preserved and ignored, and zod's default would strip them on the way out.
 */
/**
 * §11.2 — "An installed app declaring `exec.provide` registers as a provider for the commands its
 * manifest lists."
 *
 * §9.2's schema had the `exec.provide` *permission* and nowhere to list them, so the sentence had
 * no field to point at (D-167). The commands are declared **here, in the manifest**, and never
 * asked of the app at run time: the manifest is what the user saw when they installed it, and an
 * app that could name its own commands afterwards could claim `git` the day after being approved
 * for `demo`.
 *
 * The shape is a command name — a bare word, no path, no arguments — because that is all
 * resolution matches on.
 */
export const EXEC_COMMAND_PATTERN = /^[a-z0-9][a-z0-9._-]{0,31}$/;

export const manifestExecSchema = z.object({
  commands: z.array(z.string().regex(EXEC_COMMAND_PATTERN)).min(1),
});

export const manifestSchema = z
  .object({
    schema: z.literal(MANIFEST_SCHEMA_VERSION),
    id: appIdSchema,
    name: z.string().min(1),
    version: z.string().min(1),
    origin: originSchema,
    entry: httpUrlSchema,
    icons: z.array(manifestIconSchema).optional(),
    window: manifestWindowSchema.optional(),
    fileTypes: z.array(manifestFileTypeSchema).optional(),
    accepts: z.array(manifestAcceptSchema).optional(),
    menu: z.array(menuNodeSchema).optional(),
    jumpList: z.array(manifestJumpListSchema).optional(),
    permissions: z.array(z.enum(PERMISSIONS)).optional(),
    /** §11.2 — the commands this app provides. Inert without the `exec.provide` permission. */
    exec: manifestExecSchema.optional(),
    requiresAuth: z.boolean().optional(),
    theme: z.object({ followsSkin: z.boolean().optional() }).loose().optional(),
  })
  .loose();

export type AppManifest = z.infer<typeof manifestSchema>;

// ── Parsing ────────────────────────────────────────────────────────────────────────────────

export interface ManifestProblem {
  /** The zod path, dotted — `window.width`, `icons.0.size`. F11 shows this to the user. */
  path: string;
  message: string;
}

export type ManifestResult =
  | { ok: true; manifest: AppManifest }
  | { ok: false; problems: ManifestProblem[] };

function problemsOf(error: z.ZodError): ManifestProblem[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.') || '<root>',
    message: issue.message,
  }));
}

/**
 * §9.2 — parse a manifest fetched from `fetchedFrom`.
 *
 * The origin check is not a nicety: `entry` is what the shell loads in a frame, and §9.3's whole
 * trust model is "the origin is the boundary". A manifest that could name someone else's origin
 * would let any server nominate any other server as an installed app.
 */
export function parseManifest(input: unknown, fetchedFrom: string): ManifestResult {
  const parsed = manifestSchema.safeParse(input);
  if (!parsed.success) return { ok: false, problems: problemsOf(parsed.error) };

  const manifest = parsed.data;
  const served = originOf(fetchedFrom);
  const problems: ManifestProblem[] = [];

  if (served === null) {
    problems.push({ path: '<root>', message: `'${fetchedFrom}' is not a URL a manifest can come from` });
  } else if (manifest.origin !== served) {
    problems.push({
      path: 'origin',
      message: `manifest declares origin '${manifest.origin}' but was served from '${served}'`,
    });
  }

  // `entry` has to be on the app's own origin too, or the origin check on every inbound bridge
  // message would be comparing against an origin the frame is not actually running on (§9.3).
  const entryOrigin = originOf(manifest.entry);
  if (entryOrigin !== manifest.origin) {
    problems.push({
      path: 'entry',
      message: `entry '${manifest.entry}' is not on the app's origin '${manifest.origin}'`,
    });
  }

  return problems.length === 0 ? { ok: true, manifest } : { ok: false, problems };
}

/** The `/.well-known` URL for an origin. */
export function manifestUrlFor(origin: string): string {
  return `${origin.replace(/\/$/, '')}${MANIFEST_WELL_KNOWN_PATH}`;
}

/** §9.4 — what an installed catalog app is granted with no prompt, intersected with what it asked. */
export function defaultGrantsFor(manifest: AppManifest): Permission[] {
  const asked = manifest.permissions ?? [];
  return DEFAULT_GRANTED_PERMISSIONS.filter((permission) => asked.includes(permission));
}

/** §9.4 — the permissions that need a §19.4 prompt before they are granted. */
export function promptedPermissionsFor(manifest: AppManifest): Permission[] {
  const granted = new Set(defaultGrantsFor(manifest));
  return (manifest.permissions ?? []).filter((permission) => !granted.has(permission));
}
